<template>
  <button
    type="button"
    class="voting-option"
    :class="{ 'voting-option--selected': selected }"
    :role="isMultiple ? 'checkbox' : 'radio'"
    :aria-checked="selected"
    @click="$emit('select')"
  >
    <span class="voting-option__body">
      <img
        v-if="media && media.kind === 'IMAGE'"
        class="voting-option__media"
        :src="media.url"
        :alt="option.label"
      />
      <video
        v-else-if="media && media.kind === 'VIDEO'"
        class="voting-option__media voting-option__media--video"
        :src="media.url"
        :aria-label="`Material de ${option.label}`"
        muted
        preload="metadata"
        playsinline
      ></video>
      <span v-else class="voting-option__initial" aria-hidden="true">
        {{ option.label ? option.label.charAt(0).toUpperCase() : "?" }}
      </span>

      <span class="voting-option__copy">
        <span class="voting-option__label-row">
          <span class="voting-option__label">{{ option.label }}</span>
          <span
            v-if="youtubeEmbedUrl"
            class="voting-option__video-badge"
            title="Esta opción tiene un video de presentación"
          >
            <span aria-hidden="true">&#9654;</span>
            Video
          </span>
        </span>
        <span v-if="option.position" class="voting-option__position">
          {{ option.position }}
        </span>
        <span v-if="option.description" class="voting-option__description">
          {{ option.description }}
        </span>
        <span v-if="youtubeEmbedUrl" class="voting-option__youtube">
          <span class="voting-option__youtube-player">
            <iframe
              :src="youtubeEmbedUrl"
              class="voting-option__youtube-frame"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              :title="`Video de ${option.label}`"
            ></iframe>
          </span>
        </span>
      </span>

      <span
        class="voting-option__mark"
        :class="[
          isMultiple ? 'voting-option__mark--multiple' : 'voting-option__mark--single',
          { 'voting-option__mark--selected': selected },
        ]"
        aria-hidden="true"
      >
        <span v-if="selected">&#10003;</span>
      </span>
    </span>
  </button>
</template>

<script>
import { getYoutubeEmbedUrl } from "@/share/voting";

export default {
  name: "VotingOption",
  props: {
    option: { type: Object, required: true },
    selected: { type: Boolean, default: false },
    selectionMode: { type: String, default: "SINGLE" },
  },
  computed: {
    isMultiple() {
      return this.selectionMode !== "SINGLE";
    },
    media() {
      const mediaRefs = Array.isArray(this.option.mediaRefs)
        ? this.option.mediaRefs
        : [];
      const media = mediaRefs.find(
        (item) =>
          item &&
          item.url &&
          ["IMAGE", "VIDEO"].includes(String(item.kind || "").toUpperCase())
      );
      if (!media) return null;
      return { ...media, kind: String(media.kind).toUpperCase() };
    },
    youtubeUrl() {
      if (typeof this.option.videoUrl === "string" && this.option.videoUrl.trim()) {
        return this.option.videoUrl.trim();
      }

      const mediaRefs = Array.isArray(this.option.mediaRefs)
        ? this.option.mediaRefs
        : [];
      const youtubeMedia = mediaRefs.find(
        (item) =>
          item &&
          item.url &&
          String(item.kind || "").toUpperCase() === "YOUTUBE"
      );
      return youtubeMedia && typeof youtubeMedia.url === "string"
        ? youtubeMedia.url.trim()
        : "";
    },
    youtubeEmbedUrl() {
      return getYoutubeEmbedUrl(this.youtubeUrl);
    },
  },
};
</script>

<style scoped>
.voting-option {
  width: 100%;
  border: 1px solid #dfe4eb;
  border-radius: 16px;
  padding: 16px;
  background: #ffffff;
  color: #1b273e;
  text-align: left;
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease;
}

.voting-option:hover {
  border-color: rgba(255, 181, 0, 0.75);
  box-shadow: 0 8px 22px rgba(27, 39, 62, 0.08);
}

.voting-option:focus-visible {
  outline: 3px solid rgba(255, 181, 0, 0.45);
  outline-offset: 2px;
}

.voting-option--selected {
  border-color: #ffb500;
  background: #fff9e9;
  box-shadow: 0 0 0 2px rgba(255, 181, 0, 0.18);
}

.voting-option__body {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.voting-option__media,
.voting-option__initial {
  display: flex;
  flex: 0 0 80px;
  width: 80px;
  height: 80px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  object-fit: cover;
}

.voting-option__media--video {
  background: #121a2a;
}

.voting-option__initial {
  background: #e9eef5;
  color: #1b273e;
  font-size: 28px;
  font-weight: 800;
}

.voting-option__copy {
  display: block;
  min-width: 0;
  flex: 1;
}

.voting-option__label,
.voting-option__position,
.voting-option__description {
  display: block;
}

.voting-option__label-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.voting-option__label {
  font-size: 17px;
  font-weight: 800;
  line-height: 1.35;
}

.voting-option__video-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(255, 0, 0, 0.08);
  color: #c52222;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.2;
}

.voting-option__position {
  margin-top: 4px;
  color: #a66f00;
  font-size: 13px;
  font-weight: 700;
}

.voting-option__description {
  margin-top: 8px;
  color: #687386;
  font-size: 14px;
  line-height: 1.55;
}

.voting-option__youtube {
  display: block;
  margin-top: 14px;
}

.voting-option__youtube-player {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 12px;
  background: #121a2a;
}

.voting-option__youtube-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}

.voting-option__mark {
  display: flex;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  border: 2px solid #c9d0db;
  color: #1b273e;
  font-size: 15px;
  font-weight: 900;
}

.voting-option__mark--single {
  border-radius: 50%;
}

.voting-option__mark--multiple {
  border-radius: 6px;
}

.voting-option__mark--selected {
  border-color: #ffb500;
  background: #ffb500;
}

@media (max-width: 560px) {
  .voting-option {
    padding: 13px;
  }

  .voting-option__media,
  .voting-option__initial {
    flex-basis: 64px;
    width: 64px;
    height: 64px;
  }

  .voting-option__label {
    font-size: 15px;
  }
}
</style>
